"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "react-hot-toast";
import { Bell } from "lucide-react";

interface NotificationToastProps {
  title: string;
  body: string;
  url: string;
  toastId: string;
}

const NotificationToast = ({
  title,
  body,
  url,
  toastId,
}: NotificationToastProps) => {
  const router = useRouter();
  const [hovering, setHovering] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const NOTIFICATION_DECAY_TIME_IN_MILLISECONDS = 4000;

  const startTimer = () => {
    if (!timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        if (!hovering) {
          setDismissed(true);
          setTimeout(() => toast.dismiss(toastId), 300);
        }
      }, NOTIFICATION_DECAY_TIME_IN_MILLISECONDS);
    }
  };

  const clearTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    startTimer();
    return clearTimer;
  }, [hovering]);

  const handleClick = () => {
    toast.dismiss(toastId);
    router.push(url);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDismissed(true);
    setTimeout(() => toast.dismiss(toastId), 300);
  };

  return (
    <div
      onMouseEnter={() => {
        setHovering(true);
        clearTimer();
      }}
      onMouseLeave={() => {
        setHovering(false);
        startTimer();
      }}
      onClick={handleClick}
      className={`bg-primary border border-gray-200 w-[360px] max-w-full cursor-pointer rounded-xl p-4 flex items-start justify-between space-x-3 transform transition-all duration-300 ${
        dismissed
          ? "translate-x-full opacity-0"
          : "translate-y-0 translate-x-0 opacity-100"
      }`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start space-x-3">
        <Bell className="h-6 w-6 text-blue-500" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-300">
            {title}
          </div>
          <div className="mt-1 text-sm text-gray-300 dark:text-gray-600">
            {body}
          </div>
        </div>
      </div>
      <button
        onClick={handleClose}
        className="ml-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-800 transition"
        aria-label="Close notification"
      >
        ✕
      </button>
    </div>
  );
};

export default NotificationToast;
